import { useCallback, useEffect, useState } from 'react';
import ContactResource from 'Resources/Contact';
import type Contact from 'App/resources/Model/Contact';

/** What the CRM already holds for a contact: shown on its card, and not looked up again. */
export type CrmContactInfo = {
	email: string;
	cellPhone: string;
	phone: string;
	/** The contact's company in the CRM, which can differ from the employer on the profile. */
	client: { id: number; name: string } | null;
};

/**
 * The email and numbers of the contact a LinkedIn profile already is (WEB-13771). The extension
 * showed only the profile's own fields, so a rep could pay to look up an email the CRM already had.
 * `reload` re-reads it after "Add to the contact" fills something in, keeping what is shown until the
 * new values arrive. `loading` is true until the first read for a contact answered, so the card can
 * hold its place rather than jump when the values land (WEB-15179). `contact` is the record itself,
 * which the phone call and activity actions open their forms on (WEB-15181).
 */
export default function useCrmContactInfo(contactId: number | undefined) {
	// Keyed by contact, so another profile's values are never shown while its own are read.
	const [read, setRead] = useState<{
		contactId: number;
		info: CrmContactInfo | null;
		contact: Contact | null;
	} | null>(null);
	const [version, setVersion] = useState(0);

	// Cleared only for another contact: a reload keeps what is shown until the new read lands.
	useEffect(() => {
		if (!contactId) return;
		let cancelled = false;
		ContactResource.get(contactId)
			.then(({ data }) => {
				if (cancelled) return;
				setRead({
					contactId,
					info: {
						email: data.email ?? '',
						cellPhone: data.cellPhone ?? '',
						phone: data.phone ?? '',
						client: data.client ? { id: data.client.id, name: data.client.name } : null
					},
					contact: data
				});
			})
			.catch((err: unknown) => {
				console.error('[EmbedAIChat] reading the CRM contact failed', { contactId, err });
				if (!cancelled)
					setRead(current =>
						current?.contactId === contactId ? current : { contactId, info: null, contact: null }
					);
			});
		return () => {
			cancelled = true;
		};
	}, [contactId, version]);

	const reload = useCallback(() => setVersion(current => current + 1), []);
	const current = contactId && read?.contactId === contactId ? read : null;
	return {
		info: current?.info ?? null,
		contact: current?.contact ?? null,
		loading: Boolean(contactId) && !current,
		reload
	};
}
